import { DialogCancelledError, openAuthDialog } from "@features/office";

import { exchangeAddinHandoffCode } from "../api/exchangeAddinHandoffCode";
import { getCurrentUser } from "../api/getCurrentUser";
import { useAuthStore } from "../store/useAuthStore";
import { addinLoginStartUrl } from "./addinLoginStartUrl";

type LoginOutcome = {
    ok: boolean;
    cancelled?: boolean;
    detail?: string;
};

/**
 * 支援アドインのログインフローを実行する。
 *
 * Office Dialog を開いて SAML 認証を完走させる。Office デスクトップ (WebView2) では Dialog webview と
 * taskpane webview で Cookie jar が別のため、Dialog で Set-Cookie しても taskpane から見えない。
 * そこで Dialog は SAML callback 発行の **handoff コード** を messageParent で渡すだけとし、taskpane 自身が
 * `/user-tokens/addin/handoff-exchange` でコードを交換して **自 jar に Cookie を得る**
 * ([[2026-07-03 設計ノート アドイン認証 Dialog-taskpane Cookie jar 分断修正]])。
 *
 * 交換後、`/user-tokens/me` を呼んでユーザー情報を取得し (Cookie 可読性の実効チェックを兼ねる)、
 * auth store を authenticated に遷移させる。
 *
 * **必ずユーザー操作 (click 等) ハンドラ内で同期的に呼び出すこと。**
 */
export async function performLogin(): Promise<LoginOutcome> {
    try {
        const message = await openAuthDialog(addinLoginStartUrl);
        if (message.type !== "auth-success") {
            return { ok: false, detail: message.reason };
        }
        const exchanged = await exchangeAddinHandoffCode(message.code);
        if (!exchanged) {
            return { ok: false, detail: "認証コードの引き換えに失敗しました" };
        }
        const user = await getCurrentUser();
        if (!user) {
            return { ok: false, detail: "Cookie が設定されていません" };
        }
        useAuthStore.getState().setAuthenticated(user);
        return { ok: true };
    } catch (e) {
        if (e instanceof DialogCancelledError) {
            return { ok: false, cancelled: true };
        }
        return { ok: false, detail: e instanceof Error ? e.message : "unknown" };
    }
}
